import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Comment {
  id: string; file_path: string; line: number; content: string
  parent_id: string | null; resolved: number; created_at: string
  author_name: string; author_avatar: string | null; user_id: string
}

interface Props { projectId: string; filePath: string }

export function CodeComments({ projectId, filePath }: Props) {
  const [comments, setComments] = useState<Comment[]>([])
  const [newLine, setNewLine] = useState<number | null>(null)
  const [newContent, setNewContent] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')

  const load = useCallback(() => {
    authFetch(apiUrl(`/api/comments?projectId=${projectId}&filePath=${encodeURIComponent(filePath)}`))
      .then(r => r.json()).then(d => setComments(Array.isArray(d) ? d : [])).catch(() => {})
  }, [projectId, filePath])

  useEffect(() => { load() }, [load])

  const addComment = async () => {
    if (!newContent.trim() || newLine === null) return
    await authFetch(apiUrl('/api/comments'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, filePath, line: newLine, content: newContent }),
    })
    setNewContent(''); setNewLine(null); load()
  }

  const addReply = async (parentId: string, line: number) => {
    if (!replyContent.trim()) return
    await authFetch(apiUrl('/api/comments'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, filePath, line, content: replyContent, parentId }),
    })
    setReplyContent(''); setReplyTo(null); load()
  }

  const toggleResolve = async (id: string, resolved: boolean) => {
    await authFetch(apiUrl(`/api/comments/${id}`), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolved: !resolved }),
    })
    load()
  }

  const deleteComment = async (id: string) => {
    await authFetch(apiUrl(`/api/comments/${id}`), { method: 'DELETE' })
    load()
  }

  // Group by line, top-level only
  const topLevel = comments.filter(c => !c.parent_id)
  const replies = (parentId: string) => comments.filter(c => c.parent_id === parentId)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>💬</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Comments</span>
        <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{comments.length}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => setNewLine(newLine === null ? 1 : null)} style={btnStyle}>+ Add</button>
      </div>

      {/* New comment form */}
      {newLine !== null && (
        <div style={{ padding: 12, borderBottom: '1px solid var(--border)', background: 'rgba(124,110,245,0.04)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>Line:</span>
            <input type="number" value={newLine} onChange={e => setNewLine(parseInt(e.target.value) || 1)} min={1}
              style={{ width: 50, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)', fontSize: 10, fontFamily: 'var(--font-mono)' }} />
          </div>
          <textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="Write a comment..."
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)', fontSize: 11, fontFamily: 'var(--font-mono)', resize: 'vertical', height: 50, outline: 'none' }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button onClick={addComment} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>Post</button>
            <button onClick={() => setNewLine(null)} style={btnStyle}>Cancel</button>
          </div>
        </div>
      )}

      {/* Comments list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {topLevel.length === 0 && newLine === null && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            No comments on this file
          </div>
        )}
        {topLevel.map(c => (
          <div key={c.id} style={{ borderBottom: '1px solid var(--border)', opacity: c.resolved ? 0.5 : 1 }}>
            <div style={{ padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#fff', fontWeight: 700 }}>
                  {c.author_name?.charAt(0)?.toUpperCase() ?? '?'}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--text-0)' }}>{c.author_name}</span>
                <span style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'var(--font-mono)', background: 'rgba(124,110,245,0.1)', padding: '1px 5px', borderRadius: 4 }}>L{c.line}</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', lineHeight: 1.5, paddingLeft: 24 }}>{c.content}</div>
              <div style={{ display: 'flex', gap: 8, paddingLeft: 24, marginTop: 6 }}>
                <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} style={linkBtn}>Reply</button>
                <button onClick={() => toggleResolve(c.id, !!c.resolved)} style={linkBtn}>{c.resolved ? 'Unresolve' : 'Resolve'}</button>
                <button onClick={() => deleteComment(c.id)} style={{ ...linkBtn, color: 'var(--red)' }}>Delete</button>
              </div>
            </div>

            {/* Replies */}
            {replies(c.id).map(r => (
              <div key={r.id} style={{ padding: '6px 14px 6px 38px', background: 'rgba(124,110,245,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 600, color: 'var(--text-0)' }}>{r.author_name}</span>
                  <span style={{ fontSize: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}>{r.content}</div>
              </div>
            ))}

            {/* Reply form */}
            {replyTo === c.id && (
              <div style={{ padding: '6px 14px 10px 38px' }}>
                <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Reply..."
                  style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)', fontSize: 10, fontFamily: 'var(--font-mono)', resize: 'none', height: 36, outline: 'none' }} />
                <button onClick={() => addReply(c.id, c.line)} style={{ ...btnStyle, marginTop: 4, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>Post Reply</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer',
  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
}

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, padding: 0,
}

